import { useEffect, useRef, useState } from "react";
import PlatinumIcon from "../components/PlatinumIcon";
import { getRivenEvaluation } from "../utils/rivenRankingUtils";

const MIN_TIER_MEDIAN_SAMPLE = 5;

export default function AppraisalApp() {
  const GA4_ID = process.env.REACT_APP_GA4_ID;
  const BMC_URL = process.env.REACT_APP_BUYMEACOFFEE_URL;
  const PAYPAL_URL = process.env.REACT_APP_PAYPAL_URL;
  const [weaponOptions, setWeaponOptions] = useState([]);
  const [rivenAttributes, setRivenAttributes] = useState({});
  const [rivenAttributeUnits, setRivenAttributeUnits] = useState({});
  const [weapon, setWeapon] = useState(() => {
    const p = new URLSearchParams(window.location.search);
    return p.get("weapon") || "stug";
  });
  const [posFilters, setPosFilters] = useState(() => {
    const p = new URLSearchParams(window.location.search);
    const raw = p.get("pos");
    const parts = raw ? raw.split(",").filter(Boolean) : [];
    return [parts[0] || "", parts[1] || "", parts[2] || ""];
  });
  const [negFilter, setNegFilter] = useState(() => {
    const p = new URLSearchParams(window.location.search);
    return p.get("neg") || "no_negative";
  });
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rateLimitSecs, setRateLimitSecs] = useState(0);
  const [computedTierScore, setComputedTierScore] = useState(0);
  const [computedTierCaseIndex, setComputedTierCaseIndex] = useState(null);
  const [weaponMaxTier, setWeaponMaxTier] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [baseResults, setBaseResults] = useState(null);
  const [offlineBaseResults, setOfflineBaseResults] = useState(null);
  const [sniperLowestOffline, setSniperLowestOffline] = useState(null);
  const [referenceResults, setReferenceResults] = useState(null);
  const [, setReferenceMode] = useState(null);
  const [tierOnlyResults, setTierOnlyResults] = useState(null);
  const [shareCopied, setShareCopied] = useState(null);
  const [storageConsent, setStorageConsent] = useState(() => {
    const v = localStorage.getItem("appraisal_consent");
    if (v === "true") return true;
    if (v === "false") return false;
    return null;
  });
  const [searchHistory, setSearchHistory] = useState(() => {
    try {
      const consent = localStorage.getItem("appraisal_consent");
      if (consent !== "true") return [];
      return JSON.parse(localStorage.getItem("appraisal_history")) ?? [];
    } catch { return []; }
  });
  const [consentBannerDismissed, setConsentBannerDismissed] = useState(false);
  const [page, setPage] = useState("home");
  const rateLimitedUntilRef = useRef(null);
  const [goodRollsSummary, setGoodRollsSummary] = useState(null);
  const [goodRollsLoading, setGoodRollsLoading] = useState(false);
  const [goodRollIndex, setGoodRollIndex] = useState(0);

  useEffect(() => {
    fetch("/v1/riven/items")
      .then((r) => r.json())
      .then((data) => {
        const items = data?.payload?.items ?? [];
        setWeaponOptions([...items].sort((a, b) => a.item_name.localeCompare(b.item_name)));
      })
      .catch(console.error);
  }, []);

  useEffect(() => {
    fetch("/v1/riven/attributes")
      .then((r) => r.json())
      .then((data) => {
        const attrs = data?.payload?.attributes ?? [];
        const nameMap = {};
        const unitsMap = {};
        attrs.forEach((a) => {
          nameMap[a.url_name] = a.effect;
          unitsMap[a.url_name] = a.units;
        });
        setRivenAttributes(nameMap);
        setRivenAttributeUnits(unitsMap);
      })
      .catch(console.error);
  }, []);

  useEffect(() => {
    if (storageConsent !== true || !GA4_ID) return;

    if (!window.dataLayer) window.dataLayer = [];
    if (!window.gtag) {
      window.gtag = function gtag() {
        window.dataLayer.push(arguments);
      };
    }

    if (!document.getElementById("ga4-script")) {
      const script = document.createElement("script");
      script.id = "ga4-script";
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`;
      document.head.appendChild(script);
    }

    window.gtag("js", new Date());
    window.gtag("config", GA4_ID, {
      page_path: window.location.pathname + window.location.search,
    });
  }, [storageConsent, GA4_ID]);

  const sortedAttributes = Object.entries(rivenAttributes).sort(([, a], [, b]) =>
    a.localeCompare(b)
  );

  const formatAttrLabel = (url) => {
    if (!url) return '';
    if (rivenAttributes[url]) return rivenAttributes[url];
    const synonyms = {
      infested_damage: 'Damage to Infested',
      damage_vs_infested: 'Damage to Infested',
      corpus_damage: 'Damage to Corpus',
      damage_vs_corpus: 'Damage to Corpus',
      grineer_damage: 'Damage to Grineer',
      damage_vs_grineer: 'Damage to Grineer',
    };
    if (synonyms[url]) return synonyms[url];
    // generic fallback: prettify url_name
    let s = String(url).replace(/_/g, ' ');
    s = s.replace(/\bdamage vs\b/gi, 'damage to');
    return s.split(' ').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  };

  const getWeaponDisplayName = (urlName) =>
    weaponOptions.find((o) => o.url_name === urlName)?.item_name ?? urlName;

  const buildTierInputAttrs = (activePos, neg) => {
    const attrs = activePos.map((url_name) => ({ url_name, positive: true }));
    if (neg && neg !== "any") {
      attrs.push({ url_name: neg, positive: false });
    }
    return attrs;
  };

  useEffect(() => {
    let mounted = true;
    async function computeTier() {
      const activePos = posFilters.filter(Boolean);
      const attrs = buildTierInputAttrs(activePos, negFilter);
      const displayName = weaponOptions.find((o) => o.url_name === weapon)?.item_name ?? weapon;
      try {
        const evaluation = await getRivenEvaluation(attrs, displayName);
        if (!mounted) return;
        setComputedTierScore(evaluation?.score ?? 0);
        setComputedTierCaseIndex(typeof evaluation?.bestCaseIndex === 'number' ? evaluation.bestCaseIndex : null);
        const ceilings = evaluation?.caseCeilings ?? [];
        const maxTier = ceilings.length > 0 ? Math.max(...ceilings) : null;
        setWeaponMaxTier(maxTier);
      } catch (err) {
        if (!mounted) return;
        setComputedTierScore(0);
        setComputedTierCaseIndex(null);
        setWeaponMaxTier(null);
      }
    }
    computeTier();
    return () => { mounted = false; };
  }, [weapon, posFilters, negFilter, weaponOptions]);

  const computedTierLabel = computedTierScore > 0 ? `${computedTierScore}+` : `${computedTierScore}`;

  useEffect(() => {
    let mounted = true;
    async function loadSummary() {
      setGoodRollsLoading(true);
      setGoodRollsSummary(null);
      try {
        const displayName = weaponOptions.find((o) => o.url_name === weapon)?.item_name ?? weapon;
        const params = new URLSearchParams({ weapon: displayName });
        const res = await fetch(`/.netlify/functions/weaponSummary?${params.toString()}`);
        if (!res.ok) {
          const text = await res.text();
          throw new Error(text || `status ${res.status}`);
        }
        const data = await res.json();
        if (!mounted) return;
        setGoodRollsSummary(data);
        setGoodRollIndex(0);
      } catch (err) {
        if (!mounted) return;
        setGoodRollsSummary({ error: String(err) });
      } finally {
        if (!mounted) return;
        setGoodRollsLoading(false);
      }
    }
    loadSummary();
    return () => { mounted = false; };
  }, [weapon, weaponOptions]);

  const prevGoodRoll = () => setGoodRollIndex((i) => Math.max(0, i - 1));
  const nextGoodRoll = () => setGoodRollIndex((i) => {
    const len = (goodRollsSummary && Array.isArray(goodRollsSummary.goodRolls)) ? goodRollsSummary.goodRolls.length : 0;
    return Math.min(len > 0 ? len - 1 : 0, i + 1);
  });

  const statusColor = (status) => {
    if (status === "ingame") return "bg-amber-500";
    if (status === "online") return "bg-emerald-600";
    return "bg-slate-400";
  };

  const statusLabel = (status) => {
    if (status === "ingame") return "In Game";
    if (status === "online") return "Online";
    return "Offline";
  };

  const formatAttributeValue = (attr) => {
    const units = rivenAttributeUnits[attr.url_name];
    const numericValue = typeof attr.value === "number" ? attr.value : Number(attr.value);
    if (units === "multiply" && Number.isFinite(numericValue)) {
      return `x${Math.abs(numericValue).toFixed(2)}`;
    }
    const shown = Number.isFinite(numericValue)
      ? numericValue.toFixed(1)
      : attr.value;
    // Only append % if units explicitly says "percent"; null/undefined means just the number
    return units === "percent" ? `${shown}%` : shown;
  };

  const getAuctionTierScore = (auction) => auction?.evaluation?.score ?? 0;

  const filterAuctionsByTier = (auctions, targetScore) =>
    (auctions ?? []).filter((auction) => getAuctionTierScore(auction) === targetScore);

  // Positive attribute name helper removed (no longer used).

  // (Removed unused helpers: hasAnyNegativeAttribute, hasSpecificNegativeAttribute,
  // matchesExactPositiveFilters, matchesFallbackPositiveFilters)

  const copyTradeMessage = (ingameName, weaponName, itemName) => {
    if (!ingameName) return;
    navigator.clipboard.writeText(`/w ${ingameName} Hi, WTB ${weaponName} ${itemName}. Is it still available? (https://rivenappraiser.netlify.app/)`);
    setCopiedId(ingameName);
    setTimeout(() => setCopiedId((prev) => (prev === ingameName ? null : prev)), 2000);
  };

  const copyShareUrl = () => {
    const p = new URLSearchParams();
    p.set("weapon", weapon);
    if (posFilters.filter(Boolean).length > 0) p.set("pos", posFilters.filter(Boolean).join(","));
    if (negFilter) p.set("neg", negFilter);
    const url = `${window.location.origin}${window.location.pathname}?${p.toString()}`;
    navigator.clipboard.writeText(url);
    setShareCopied("url");
    setTimeout(() => setShareCopied((prev) => prev === "url" ? null : prev), 2000);
  };

  const copyTextSummary = () => {
    if (!results) return;
    const weaponName = weaponOptions.find((o) => o.url_name === weapon)?.item_name ?? weapon;
    const posNames = posFilters.filter(Boolean).map((p) => rivenAttributes[p] ?? p).join(", ");
    const negName = negFilter === 'no_negative' ? 'no negative' : (negFilter && negFilter !== "any" ? (rivenAttributes[negFilter] ?? negFilter) : negFilter === "any" ? "any" : "none");
    const sameTierResults = filterAuctionsByTier(results, computedTierScore);
    const sameTierReferenceResults = filterAuctionsByTier(referenceResults, computedTierScore);
    const usingReference = sameTierResults.length === 0 && sameTierReferenceResults.length > 0;
    const tierSample = (tierOnlyResults ?? []).slice(0, 20);
    const tierPrices = tierSample.map((a) => a.buyout_price).sort((a, b) => a - b);
    const tierMid = Math.floor(tierPrices.length / 2);
    const tierMedian = tierPrices.length === 0
      ? null
      : tierPrices.length % 2 !== 0
      ? tierPrices[tierMid]
      : Math.round((tierPrices[tierMid - 1] + tierPrices[tierMid]) / 2);
    const pool = sameTierResults.length > 0 ? sameTierResults : sameTierReferenceResults;
    const prices = [...pool.map((a) => a.buyout_price)].sort((a, b) => a - b);
    const top5 = prices.slice(0, 5);
    const mid = Math.floor(top5.length / 2);
    const median = top5.length > 0
      ? (top5.length % 2 !== 0 ? top5[mid] : Math.round((top5[mid - 1] + top5[mid]) / 2))
      : tierMedian;
    const minAsk = prices.length > 0 ? prices[0] : tierMedian;
    const lines = [
      `**Riven Appraisal — ${weaponName}**`,
      posNames ? `Positives: ${posNames}` : "",
      negName !== "none" ? `Negative: ${negName}` : "",
      ``,
      `Results: ${results.length} listing(s)`,
      `Same tier (${computedTierLabel}) in filtered results: ${sameTierResults.length}`,
      usingReference ? `Using fallback pool: negative any (${sameTierReferenceResults.length} listing(s))` : "",
      minAsk !== null ? `Min ask: ${minAsk} pt` : "",
      minAsk !== null && median !== null ? (minAsk === median ? `Estimated value: ${minAsk} pt` : `Estimated value: ${minAsk} – ${median} pt`) : "",
      sameTierResults.length === 0 && tierMedian !== null ? `No same-tier matches in filtered list; using tier-only median (${tierMedian} pt) as minimum fallback.` : "",
      ``,
      `Source: ${window.location.origin}${window.location.pathname}`,
    ].filter((l) => l !== undefined);
    navigator.clipboard.writeText(lines.join("\n"));
    setShareCopied("text");
    setTimeout(() => setShareCopied((prev) => prev === "text" ? null : prev), 2000);
  };

  const saveToHistory = (w, pos, neg) => {
    if (storageConsent !== true) return;
    const entry = { weapon: w, posFilters: pos.filter(Boolean), negFilter: neg };
    const key = [w, ...pos.filter(Boolean).slice().sort(), neg].join("|");
    setSearchHistory((prev) => {
      const deduped = prev.filter(
        (e) => [e.weapon, ...e.posFilters.slice().sort(), e.negFilter].join("|") !== key
      );
      const next = [entry, ...deduped].slice(0, 5);
      localStorage.setItem("appraisal_history", JSON.stringify(next));
      return next;
    });
  };

  const updatePosFilter = (idx, val) =>
    setPosFilters((prev) => prev.map((v, i) => (i === idx ? val : v)));

  const search = async () => {
    if (rateLimitedUntilRef.current && Date.now() < rateLimitedUntilRef.current) return;
    setLoading(true);
    setResults(null);
    setReferenceResults(null);
    setReferenceMode(null);
    setTierOnlyResults(null);
    setBaseResults(null);
    setOfflineBaseResults(null);
    setSniperLowestOffline(null);
    try {
      const activePos = posFilters.filter(Boolean);
      // compute tier target score using server-side evaluator
      const tierEval = await getRivenEvaluation(buildTierInputAttrs(activePos, negFilter), getWeaponDisplayName(weapon));
      const tierTargetScore = tierEval?.score ?? 0;

      

      const applyRateLimit = () => {
        const until = Date.now() + 60_000;
        rateLimitedUntilRef.current = until;
        setRateLimitSecs(60);
        const timer = setInterval(() => {
          const rem = Math.ceil((until - Date.now()) / 1000);
          if (rem <= 0) {
            clearInterval(timer);
            rateLimitedUntilRef.current = null;
            setRateLimitSecs(0);
          } else {
            setRateLimitSecs(rem);
          }
        }, 500);
      };

      // Primary filtered results via serverless sniper function (annotated with evaluation)
      const sniperRes = await fetch('/.netlify/functions/sniper', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ weaponName: weapon, displayName: getWeaponDisplayName(weapon), posFilters: activePos, negFilter, pageLimit: 10 }),
      });
      if (sniperRes.status === 429) {
        applyRateLimit();
        setLoading(false);
        return;
      }
      const sniperData = await sniperRes.json();
      const primaryAuctions = sniperData.auctions ?? [];
      setSniperLowestOffline(sniperData.lowestOfflinePrice ?? null);

      let fallbackReference = null;
      let fallbackMode = null;
      if (primaryAuctions.length === 0 && negFilter && negFilter !== 'any' && negFilter !== 'no_negative') {
        const fallbackRes = await fetch('/.netlify/functions/sniper', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ weaponName: weapon, displayName: getWeaponDisplayName(weapon), posFilters: activePos, negFilter: 'any', pageLimit: 10 }),
        });
        if (fallbackRes.status === 429) { applyRateLimit(); setLoading(false); return; }
        const fallbackData = await fallbackRes.json();
        fallbackReference = fallbackData.auctions ?? [];
        fallbackMode = fallbackReference.length > 0 ? 'any' : null;
      }

      // Tier-only pool: fetch weapon listings (no pos/neg filters) and filter by evaluation
      const tierOnlyRes = await fetch('/.netlify/functions/sniper', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ weaponName: weapon, displayName: getWeaponDisplayName(weapon), posFilters: [], negFilter: '', pageLimit: 10 }),
      });
      if (tierOnlyRes.status === 429) { applyRateLimit(); setLoading(false); return; }
      const tierOnlyData = await tierOnlyRes.json();
      const tierOnlyAuctions = (tierOnlyData.auctions ?? []).filter((a) => (a.evaluation?.score ?? 0) >= tierTargetScore);

      setTierOnlyResults(tierOnlyAuctions);
      setResults(primaryAuctions);
      setReferenceResults(fallbackReference);
      setReferenceMode(fallbackMode);
      saveToHistory(weapon, posFilters, negFilter);

      try {
        const baseParams = new URLSearchParams({
          type: "riven",
          weapon_url_name: weapon,
          sort_by: "price_asc",
        });
        const baseRes = await fetch(`/v1/auctions/search?${baseParams.toString()}`);
        if (baseRes.ok) {
          const baseData = await baseRes.json();
          const allBase = (baseData.payload.auctions ?? []).filter((a) => a.buyout_price !== null);
          const base = allBase
            .filter((a) => a.owner?.status === "online" || a.owner?.status === "ingame")
            .slice(0, 10);
          const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;
          const sevenDaysAgo = Date.now() - SEVEN_DAYS_MS;
          const baseOffline = allBase
            .filter((a) => a.owner?.status !== "online" && a.owner?.status !== "ingame")
            .filter((a) => {
              const ts = a.updated || a.updated_at || a.updatedAt || a.created_at || a.createdAt || a.created;
              if (!ts) return false;
              const t = new Date(ts).getTime();
              return !Number.isNaN(t) && t <= sevenDaysAgo;
            })
            .slice(0, 10);
          setBaseResults(base);
          setOfflineBaseResults(baseOffline);
        }
      } catch {}
    } catch (err) {
      console.error(err);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const hasSearchContext = (results !== null || baseResults !== null) && !loading;
  // weaponDisplayName is available via getWeaponDisplayName(weapon) when needed
  const exactListings = results ?? [];
  const fallbackListings = referenceResults ?? [];
  const exactSameTierListings = filterAuctionsByTier(exactListings, computedTierScore);
  const fallbackSameTierListings = filterAuctionsByTier(fallbackListings, computedTierScore);
  const pricingPoolListings = exactSameTierListings.length > 0 ? exactSameTierListings : fallbackSameTierListings;
  const usingFallbackPool = exactSameTierListings.length === 0 && fallbackSameTierListings.length > 0;
  const pricingPoolSourceLabel = pricingPoolListings.length === 0
    ? "no same-tier listings"
    : usingFallbackPool
    ? "fallback (negative any)"
    : "exact filters";

  const tierSample = (tierOnlyResults ?? []).slice(0, 20);
  const tierPrices = tierSample.map((a) => a.buyout_price).sort((a, b) => a - b);
  const tierMid = Math.floor(tierPrices.length / 2);
  const tierMedian = tierPrices.length === 0
    ? null
    : tierPrices.length % 2 !== 0
    ? tierPrices[tierMid]
    : Math.round((tierPrices[tierMid - 1] + tierPrices[tierMid]) / 2);

  const weaponMinPrice = (baseResults ?? []).length > 0
    ? Math.min(...baseResults.map((a) => a.buyout_price))
    : null;
  const unrolledListings = (baseResults ?? []).filter((a) => (a.item?.re_rolls ?? 0) === 0);
  const unrolledMinPrice = unrolledListings.length > 0
    ? Math.min(...unrolledListings.map((a) => a.buyout_price))
    : null;
  const offlineMinPrice = sniperLowestOffline != null
    ? sniperLowestOffline
    : ((offlineBaseResults ?? []).length > 0
      ? Math.min(...offlineBaseResults.map((a) => a.buyout_price))
      : null);
  const yourFilterMinPrice = pricingPoolListings.length > 0
    ? Math.min(...pricingPoolListings.map((a) => a.buyout_price))
    : tierMedian;

  return (
    <div className="min-h-screen bg-transparent text-slate-100 flex flex-col">
      {rateLimitSecs > 0 && (
        <div className="bg-amber-600 text-white text-sm text-center px-4 py-2 font-semibold flex items-center justify-center gap-1 shadow-sm">
          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" /></svg>
          Too many requests — resuming in <span className="font-bold ml-1">{rateLimitSecs}s</span>...
        </div>
      )}

      <header className="bg-slate-950/95 text-slate-50 px-6 py-4 shadow-lg shadow-slate-900/10 border-b border-sky-500/20 backdrop-blur">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-4">
            <h1 className="text-lg font-bold tracking-wide flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" /></svg>
              Warframe Riven Appraiser
            </h1>
            <nav className="flex items-center gap-1">
              <button
                onClick={() => setPage("home")}
                className={`text-sm px-3 py-1.5 rounded-lg font-medium transition ${
                  page === "home"
                    ? "bg-sky-500/20  "
                    : "  hover:text-slate-50 hover:bg-slate-800"
                }`}
              >
                Home
              </button>
              <button
                onClick={() => setPage("about")}
                className={`text-sm px-3 py-1.5 rounded-lg font-medium transition ${
                  page === "about"
                    ? "bg-sky-500/20  "
                    : "  hover:text-slate-50 hover:bg-slate-800"
                }`}
              >
                About
              </button>
            </nav>
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            {PAYPAL_URL && (
              <a
                href={PAYPAL_URL}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 rounded-lg bg-sky-500 hover:bg-sky-400 text-white text-xs font-semibold px-3 py-2 transition shadow-sm"
              >
                Donate with PayPal
              </a>
            )}
            {BMC_URL && (
              <a
                href={BMC_URL}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 rounded-lg bg-amber-400 hover:bg-amber-300 text-slate-950 text-xs font-semibold px-3 py-2 transition shadow-sm"
              >
                Buy me a coffee
              </a>
            )}
          </div>
        </div>
      </header>

      {page === "about" && (
        <div className="flex-1 flex items-start justify-center p-6 overflow-auto">
          <div className="max-w-xl w-full bg-slate-950/90 rounded-2xl shadow-lg border border-slate-800 p-8 flex flex-col gap-6 backdrop-blur">
            <div>
              <h2 className="text-xl font-bold text-slate-100 mb-1">About this project</h2>
              <div className="h-0.5 w-12 bg-sky-500 rounded-full mb-4" />
              <p className="text-slate-300 leading-relaxed">
                This is a <span className="font-semibold text-slate-100">fan-made</span> project, created without any affiliation with Digital Extremes or the official Warframe team.
                It is completely <span className="font-semibold text-slate-100">non-profit</span> and exists solely to help new players
                understand and navigate the Warframe riven market — a notoriously complex economy that can
                be daunting for newcomers.
              </p>
            </div>
            <div className="flex flex-col gap-1">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Created by</p>
              <p className="text-slate-100 font-semibold text-base">RivenHelper</p>
            </div>
            <div className="flex flex-col gap-1">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Contact</p>
              <a
                href="mailto:helder.santos8@hotmail.com"
                className="text-sky-300 hover:text-sky-200 font-medium transition"
              >
                helder.santos8@hotmail.com
              </a>
            </div>
          </div>
        </div>
      )}

      {page === "home" && <div className="flex flex-col lg:flex-row flex-1 overflow-auto lg:overflow-hidden p-4 gap-4">
        <div className="w-full lg:w-72 lg:flex-shrink-0 flex flex-col gap-4 lg:max-h-[calc(100vh-90px)] lg:overflow-y-auto">
        <div className="bg-slate-950/90 rounded-2xl shadow-lg shadow-slate-950/40 border border-slate-800 p-4 flex flex-col gap-4 flex-shrink-0 backdrop-blur">
          <div>
            <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">Weapon</label>
            <select
              value={weapon}
              onChange={(e) => setWeapon(e.target.value)}
              className="w-full border border-slate-700 rounded-lg px-3 py-1 text-sm bg-slate-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-sky-500/30 focus:border-sky-500 text-slate-100"
            >
              {weaponOptions.map((opt) => (
                <option key={opt.url_name} value={opt.url_name}>{opt.item_name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">Positive Attributes</label>
            <div className="flex flex-col">
              {[0, 1, 2].map((idx) => (
                <div key={idx} className="mb-1">
                  <select
                    value={posFilters[idx] || ""}
                    onChange={(e) => updatePosFilter(idx, e.target.value)}
                    className="w-full border border-slate-700 rounded-lg px-2 py-1 text-sm bg-slate-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-sky-500/30 focus:border-sky-500 text-slate-100"
                  >
                    <option value="">None</option>
                    {sortedAttributes.map(([url, label]) => (
                      <option key={url} value={url}>{label}</option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">Negative Attribute</label>
            <select
              value={negFilter}
              onChange={(e) => setNegFilter(e.target.value)}
              className="w-full border border-slate-700 rounded-lg px-2 py-1 text-sm bg-slate-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-sky-500/30 focus:border-sky-500 text-slate-100"
            >
              <option value="no_negative">No Negative</option>
              <option value="any">— Has negative —</option>
              {sortedAttributes.map(([url, label]) => (
                <option key={url} value={url}>{label}</option>
              ))}
            </select>
          </div>

          <div className="rounded-lg border border-violet-800 bg-violet-950/60 px-3 py-2">
            <p className="text-[11px] uppercase tracking-wide font-semibold text-violet-300">Calculated Tier</p>
            <div className="flex items-center gap-2">
              <p className="text-sm font-bold text-violet-100 mt-0.5">{computedTierLabel}</p>
              {computedTierCaseIndex !== null && goodRollsSummary?.goodRolls && (
                <span className="text-sm  text-violet-100 mt-0.5 ml-2">Case {computedTierCaseIndex + 1}</span>
              )}
            </div>
            <p className="text-[11px] text-violet-300/80 mt-0.5">Automatically calculated based on the selected attributes.</p>
            {weaponMaxTier !== null && (
              <p className="text-[11px] text-violet-300/80 mt-0.5">Max tier: {weaponMaxTier}</p>
            )}
          </div>

          <div className="mt-2">
            <div className="rounded-lg border border-slate-800 bg-slate-900/80 p-3">
              <div className="flex items-center justify-between">
                <div className="text-xs font-semibold text-slate-300">Good-roll case</div>
                <div className="flex items-center gap-2">
                  {goodRollsLoading && <div className="text-xs text-slate-400">loading…</div>}
                  {!goodRollsLoading && goodRollsSummary?.goodRolls && goodRollsSummary.goodRolls.length > 1 && (
                    <div className="flex items-center gap-1">
                      <button onClick={prevGoodRoll} disabled={goodRollIndex === 0} className="text-xs px-2 py-0.5 rounded border border-slate-700 bg-slate-900/60 text-slate-200">‹</button>
                      <span className="text-xs text-slate-400">{goodRollIndex + 1}/{goodRollsSummary.goodRolls.length}</span>
                      <button onClick={nextGoodRoll} disabled={goodRollIndex + 1 === goodRollsSummary.goodRolls.length} className="text-xs px-2 py-0.5 rounded border border-slate-700 bg-slate-900/60 text-slate-200">›</button>
                    </div>
                  )}
                </div>
              </div>

              {!goodRollsLoading && goodRollsSummary?.error && (
                <div className="text-xs text-rose-400 mt-2">Error loading good-rolls: {goodRollsSummary.error}</div>
              )}

              {!goodRollsLoading && goodRollsSummary && !goodRollsSummary.error && (
                <div className="mt-2 text-xs text-slate-300">
                  {goodRollsSummary.goodRolls && goodRollsSummary.goodRolls.length > 0 ? (
                        (() => {
                          const roll = goodRollsSummary.goodRolls[goodRollIndex] || { mandatory: [], optional: [] };
                          return (
                            <div>
                              <div className="text-xs">
                                <div className="font-semibold">Mandatory:</div>
                                <ul className="list-disc list-inside ml-4">
                                  {(roll.mandatory || []).length > 0 ? (roll.mandatory || []).map((u, i) => (
                                    <li key={`mandatory-${i}`}>{formatAttrLabel(u)}</li>
                                  )) : <li>—</li>}
                                </ul>
                              </div>
                              <div className="text-xs mt-1">
                                <div className="font-semibold">Good:</div>
                                <ul className="list-disc list-inside ml-4">
                                  {(roll.optional || []).length > 0 ? (roll.optional || []).map((u, i) => (
                                    <li key={`optional-${i}`}>{formatAttrLabel(u)}</li>
                                  )) : <li>—</li>}
                                </ul>
                              </div>
                              <div className="text-xs mt-2">
                                <div className="font-semibold">Best Bad Attributes:</div>
                                <ul className="list-disc list-inside ml-4">
                                  {(goodRollsSummary.accepted || []).length > 0 ? (goodRollsSummary.accepted || []).map((a, i) => (
                                    <li key={`accepted-${i}`}>{formatAttrLabel(a)}</li>
                                  )) : <li>None</li>}
                                </ul>
                              </div>
                            </div>
                          );
                        })()
                      ) : (
                        <div className="text-xs text-slate-400 mt-1">No explicit good-roll cases for this weapon.</div>
                      )}
                </div>
              )}
            </div>
          </div>

          <button
            onClick={search}
            disabled={loading || rateLimitSecs > 0}
            className="w-full bg-sky-600 hover:bg-sky-500 disabled:bg-slate-700 text-white text-sm font-semibold py-2.5 rounded-lg transition shadow-sm"
          >
            {loading ? "Searching..." : rateLimitSecs > 0 ? `Wait ${rateLimitSecs}s...` : (
              <span className="flex items-center justify-center gap-1">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" /></svg>
                Search
              </span>
            )}
          </button>

          {results !== null && !loading && (
            <p className="text-xs text-slate-400 text-center font-medium">
              api: {results.length} · pricing pool: {pricingPoolListings.length} ({pricingPoolSourceLabel})
            </p>
          )}
        </div>

        {storageConsent === true && searchHistory.length > 0 && (
          <div className="bg-slate-950/90 rounded-2xl shadow-lg shadow-slate-950/40 border border-slate-800 p-3 flex-shrink-0 backdrop-blur">
            <p className="text-xs text-slate-400 font-semibold mb-2 uppercase tracking-wide">Recent searches</p>
            <div className="flex flex-col gap-1">
              {searchHistory.map((entry, i) => {
                const name = getWeaponDisplayName(entry.weapon);
                const posNames = entry.posFilters.map((p) => rivenAttributes[p] ?? p);
                return (
                  <div key={i} className="flex items-center gap-1 group">
                    <button
                      onClick={() => {
                        setWeapon(entry.weapon);
                        setPosFilters(entry.posFilters.length > 0 ? entry.posFilters : []);
                        setNegFilter(entry.negFilter);
                      }}
                      className="flex-1 text-left text-xs px-2.5 py-2 rounded-lg bg-slate-900 hover:bg-slate-800 border border-slate-700 hover:border-sky-500 transition truncate"
                    >
                      <span className="font-semibold text-slate-100">{name}</span>
                      {posNames.length > 0 && (
                        <span className="text-slate-400 ml-1">· {posNames.join(", ")}</span>
                      )}
                    </button>
                    <button
                      onClick={() => {
                        setSearchHistory((prev) => {
                          const next = prev.filter((_, idx) => idx !== i);
                          localStorage.setItem("appraisal_history", JSON.stringify(next));
                          return next;
                        });
                      }}
                      className="opacity-0 group-hover:opacity-100 text-slate-500 hover:text-red-400 transition text-xs leading-none flex-shrink-0 px-1"
                      title="Remove"
                    >✕</button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        </div>

        <div className="w-full lg:w-72 lg:flex-shrink-0 bg-slate-950/90 rounded-2xl shadow-lg shadow-slate-950/40 border border-slate-800 overflow-y-auto p-4 backdrop-blur lg:max-h-[calc(100vh-90px)]">
          {results === null && !loading && (
            <p className="text-xs text-slate-400 text-center py-4 font-medium">Select a weapon and desired attributes, then click Search.</p>
          )}
          {loading && (
            <p className="text-xs text-sky-600 text-center animate-pulse py-4 font-semibold">Loading...</p>
          )}
          {results !== null && !loading && pricingPoolListings.length === 0 && (
            <div className="py-4 text-center">
              <p className="text-xs text-slate-400 font-medium">No same-tier listings to show in pricing pool.</p>
              <p className="text-[11px] text-slate-500 mt-1">Using tier-only median fallback for valuation.</p>
            </div>
          )}
          {results !== null && !loading && pricingPoolListings.length > 0 && (
            <>
              <div className="mb-2 rounded-lg border border-sky-800 bg-sky-950/50 px-2.5 py-1.5">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-sky-300">List used for pricing</p>
                <p className="text-xs text-sky-300 mt-0.5">{pricingPoolListings.length} listing(s) · {pricingPoolSourceLabel} · tier {computedTierLabel}</p>
              </div>
            <ul className="space-y-2">
              {pricingPoolListings.map((auction) => {
                const attrs = auction.item?.attributes ?? [];
                const evalResult = auction.evaluation ?? { score: 0, label: '', badgeClass: '' };
                return (
                  <li
                    key={auction.id}
                    className="rounded-xl p-3 text-sm leading-normal border bg-slate-900 border-slate-700"
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-semibold text-slate-100">
                        <span className="inline-flex items-center gap-1">
                          {auction.buyout_price ?? "—"}
                          <PlatinumIcon className="w-3.5 h-3.5" />
                          <span className={`text-[10px] font-bold rounded px-1 py-0.5 ${evalResult.badgeClass}`}>
                            {evalResult.score > 0 ? `${evalResult.score}+` : evalResult.score} {evalResult.label}
                          </span>
                        </span>
                      </span>
                      <span className="flex items-center gap-1">
                        <span className={`inline-block w-2 h-2 rounded-full ${statusColor(auction.owner?.status)}`} />
                        <span className="text-xs text-slate-400 font-medium">{statusLabel(auction.owner?.status)}</span>
                      </span>
                    </div>
                    <button
                      onClick={() => copyTradeMessage(
                        auction.owner?.ingame_name,
                        getWeaponDisplayName(weapon),
                        auction.item?.name ?? "riven"
                      )}
                      className="text-xs leading-normal text-slate-300 hover:text-sky-300 truncate text-left w-full"
                      title="Copy trade message"
                    >
                      {copiedId === auction.owner?.ingame_name ? "✓ Copied!" : auction.owner?.ingame_name}
                    </button>
                    <div className="mt-0.5 text-xs leading-normal text-slate-400">Rolls: {auction.item?.re_rolls ?? 0}</div>
                    {attrs.length > 0 && (
                      <div className="flex flex-col gap-0.5 mt-1 mb-1">
                        {attrs.map((attr, i) => {
                          const tier = attr.tier;
                          const grade = attr.grade;
                          return (
                            <div
                              key={i}
                              className={`flex items-center justify-between text-xs px-1.5 py-0.5 rounded ${
                                attr.positive ? "bg-green-950/60 text-green-200" : "bg-red-950/60 text-red-200"
                              }`}
                            >
                              <span className="flex items-center gap-1 flex-wrap">
                                {grade && (
                                    <span className="font-bold text-[10px] text-slate-200 bg-slate-700 rounded px-1">
                                    {grade}
                                  </span>
                                )}
                                <span>{rivenAttributes[attr.url_name] ?? attr.url_name}</span>
                                <span>
                                  {rivenAttributeUnits[attr.url_name] === "multiply" ? "" : (attr.positive ? "+" : "")}
                                  {formatAttributeValue(attr)}
                                </span>
                              </span>
                              {tier === "S" && (
                                <span className="ml-1 font-bold text-[10px] bg-amber-900/60 text-amber-200 rounded px-1">++</span>
                              )}
                              {tier === "A" && (
                                <span className="ml-1 font-bold text-[10px] bg-sky-900/60 text-sky-200 rounded px-1">+</span>
                              )}
                              {tier === "ok" && attr.positive === false && (
                                <span className="ml-1 font-bold text-[10px] bg-red-900/60 text-red-200 rounded px-1">++</span>
                              )}
                              {tier === "badA" && attr.positive === false && (
                                <span className="ml-1 font-bold text-[10px] bg-orange-900/60 text-orange-200 rounded px-1">-</span>
                              )}
                              {tier === "badS" && attr.positive === false && (
                                <span className="ml-1 font-bold text-[10px] bg-rose-900/60 text-rose-200 rounded px-1">--</span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    <a
                      href={`https://warframe.market/auction/${auction.id}`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-block mt-0.5 text-xs font-semibold text-sky-300 hover:underline"
                    >
                      View auction ↗
                    </a>
                  </li>
                );
              })}
            </ul>
            </>
          )}
        </div>

        <div className="flex-1 min-w-0 bg-slate-950/90 rounded-2xl shadow-lg shadow-slate-950/40 border border-slate-800 p-4 overflow-y-auto backdrop-blur lg:max-h-[calc(100vh-90px)]">
          <div className="flex items-start gap-3 bg-amber-950/50 border border-amber-800 rounded-xl p-4">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-amber-300 flex-shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <div>
              <h3 className="text-sm font-semibold text-amber-100 mb-1">Estimate only</h3>
              <p className="text-xs text-amber-100/90 leading-relaxed">
                Results are based on live listings from players who are currently online or in-game at the time of the search.
                Prices can change rapidly — sellers may go offline, update their prices, or remove their listings at any moment.
              </p>
              <p className="text-xs text-amber-100/90 leading-relaxed mt-2">
                This is a free to use tool as a <span className="font-medium">starting reference</span>, not a definitive valuation.
                Always do your own research before buying or selling rivens.
              </p>
              <p className="text-xs text-amber-100/90 leading-relaxed mt-2">
                If you want to support the project, consider sharing it with your friends or contributing via the links in the header. It helps a lot and is much appreciated! 🙏
              </p>
            </div>
          </div>

          {hasSearchContext && (
            <div className="mt-4 border border-slate-800 rounded-2xl bg-slate-900 overflow-hidden">
              <div className="px-3 py-2 border-b border-slate-800 bg-slate-900/80">
                <p className="text-xs text-slate-400 uppercase tracking-wide font-semibold">Price snapshot</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-900/70 text-slate-400 text-[11px] uppercase tracking-wide">
                    <tr>
                      <th className="text-left px-3 py-2 font-semibold">Metric</th>
                      <th className="text-left px-3 py-2 font-semibold">Value</th>
                      <th className="text-left px-3 py-2 font-semibold">Details</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800">
                    <tr>
                      <td className="px-3 py-2 font-medium text-slate-300">Min price (weapon)</td>
                      <td className="px-3 py-2 text-slate-100 font-semibold">
                        {weaponMinPrice !== null ? (
                          <span className="inline-flex items-center gap-1">{weaponMinPrice}<PlatinumIcon className="w-4 h-4" /></span>
                        ) : (
                          <span className="text-slate-500 italic">N/A</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-400">any rolls · online</td>
                    </tr>
                    <tr>
                      <td className="px-3 py-2 font-medium text-slate-300">Min price (unrolled)</td>
                      <td className="px-3 py-2 text-slate-100 font-semibold">
                        {unrolledMinPrice !== null ? (
                          <span className="inline-flex items-center gap-1">{unrolledMinPrice}<PlatinumIcon className="w-4 h-4" /></span>
                        ) : (
                          <span className="text-slate-500 italic">N/A</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-400">0 rolls · online</td>
                    </tr>
                    <tr>
                      <td className="px-3 py-2 font-medium text-slate-300">Min price (offline)</td>
                      <td className="px-3 py-2 text-slate-200 font-semibold">
                        {offlineMinPrice !== null ? (
                          <span className="inline-flex items-center gap-1">{offlineMinPrice}<PlatinumIcon className="w-4 h-4" /></span>
                        ) : (
                          <span className="text-slate-500 italic">N/A</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-400">any rolls · offline · até 7d</td>
                    </tr>
                    <tr className="bg-sky-950/40">
                      <td className="px-3 py-2 font-medium text-sky-300">Min price (your filter)</td>
                      <td className="px-3 py-2 text-sky-200 font-semibold">
                        {yourFilterMinPrice !== null ? (
                          <span className="inline-flex items-center gap-1">{yourFilterMinPrice}<PlatinumIcon className="w-4 h-4" /></span>
                        ) : (
                          <span className="text-slate-500 italic">N/A</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs text-sky-300">
                        {pricingPoolListings.length > 0
                          ? `${pricingPoolListings.length} listing(s) · ${pricingPoolSourceLabel} · tier ${computedTierLabel}`
                          : `No same-tier in filtered list · fallback to tier-only median (${computedTierLabel})`}
                      </td>
                    </tr>
                    <tr className="bg-violet-950/40">
                      <td className="px-3 py-2 font-medium text-violet-300">Median (tier-only)</td>
                      <td className="px-3 py-2 text-violet-200 font-semibold">
                        {tierMedian !== null ? (
                          <span className="inline-flex items-center gap-1">{tierMedian}<PlatinumIcon className="w-4 h-4" /></span>
                        ) : (
                          <span className="text-slate-500 italic">N/A</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs text-violet-300">
                        {tierMedian !== null ? `tier ${computedTierLabel} · weapon only · first ${tierSample.length} listing(s)` : "No tier-only results"}
                        {tierMedian !== null && tierSample.length < MIN_TIER_MEDIAN_SAMPLE && (
                            <span className="italic text-violet-300/80"> · low sample ({MIN_TIER_MEDIAN_SAMPLE}+ recommended)</span>
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {hasSearchContext && (() => {
            const comparablePool = pricingPoolListings;
            const usingReference = usingFallbackPool;
            const usingTierMedianFallback = comparablePool.length === 0 && tierMedian !== null;
            // Never fall back to basePool (any-attribute listings) — it skews prices downward
            if (comparablePool.length === 0 && !usingTierMedianFallback) return null;
            const pool = comparablePool.length > 0 ? comparablePool : [];
            const prices = [...pool.map((a) => a.buyout_price)].sort((a, b) => a - b);
            const minP = usingTierMedianFallback ? (weaponMinPrice ?? tierMedian) : prices[0];
            const medianSource = usingTierMedianFallback
              ? [tierMedian]
              : usingReference
              ? (prices.slice(1, 6).length > 0 ? prices.slice(1, 6) : [minP])
              : prices.slice(0, 5);
            const mid = Math.floor(medianSource.length / 2);
            const median = medianSource.length % 2 !== 0
              ? medianSource[mid]
              : Math.round((medianSource[mid - 1] + medianSource[mid]) / 2);
            const fromFiltered = exactSameTierListings.length > 0;
            const estimatedSourceLabel = usingTierMedianFallback
              ? "tier-only median fallback"
              : usingReference
              ? "fallback (negative any)"
              : "exact filters";
            return (
              <div className="mt-3 bg-gradient-to-br from-emerald-950/70 to-cyan-950/70 border border-emerald-800 rounded-2xl p-5 shadow-sm">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <p className="text-xs text-emerald-300 uppercase tracking-wide font-semibold">Estimated value</p>
                  <span className="text-[10px] px-2 py-0.5 rounded-full border border-emerald-800 bg-emerald-950 text-emerald-200 font-semibold uppercase tracking-wide">
                    {estimatedSourceLabel}
                  </span>
                </div>
                {(() => {
                  const gray = '#9CA3AF';
                  const light = '#34D399';
                  const dark = '#059669';
                  const mainValue = (minP == null && median == null) ? null : (minP == null ? median : (median == null ? minP : (minP < median ? minP : Math.min(minP, median))));
                  const showRange = median != null && mainValue != null && median !== mainValue;
                  const offlineVal = offlineMinPrice;
                  const onlineVal = mainValue;
                  const maxVal = median;
                  const hasRange = offlineVal != null && maxVal != null && maxVal > offlineVal;
                  const onlinePercent = hasRange ? Math.max(0, Math.min(100, Math.round(((Number(onlineVal) - Number(offlineVal)) / (Number(maxVal) - Number(offlineVal))) * 100))) : 50;
                  const showOfflineLeft = offlineVal != null && offlineVal < (minP ?? Infinity);

                  return (
                    <>
                      <div className="flex items-end gap-3 mb-3">
                        <p className="text-4xl font-extrabold text-emerald-100 tracking-tight flex items-center gap-3">
                          {showOfflineLeft && (
                            <><span className="text-4xl font-extrabold text-slate-400 inline-flex items-center">
                              {offlineVal}
                            </span><span className="text-4xl font-extrabold  text-slate-400 mx-2">–</span></>
                          )}
                          <span>{mainValue != null ? mainValue : '—'}</span>
                          {showRange && (
                            <>
                              <span className="text-4xl font-extrabold text-emerald-600 mx-2">–</span>
                              <span className="text-4xl font-extrabold">{median}</span>
                            </>
                          )}
                          <PlatinumIcon className="w-6 h-6 ml-2 text-emerald-700 inline-block" />
                        </p>
                      </div>

                      <div className="w-full">
                        <div className="relative w-full" style={{ height: '3.5rem' }}>
                          {/* gradient line at the top */}
                          <div className="absolute left-0 right-0" style={{ top: '0.375rem', height: '0.25rem' }}>
                            <div className="w-full h-full bg-slate-700 rounded-full" />
                            <div className="absolute left-0 top-0 w-full h-full rounded-full" style={{ background: `linear-gradient(to right, ${gray} 0%, ${light} ${onlinePercent}%, ${dark} 100%)` }} />
                          </div>

                          {/* markers below the line */}
                          <div className="absolute inset-0 pointer-events-none">
                            {offlineVal != null && (
                              <div style={{ left: '0%', top: '0.875rem' }} className="absolute flex flex-col items-start transform -translate-x-0">
                                <span className="text-[11px] text-emerald-300">{offlineVal}</span>
                                <span className="text-[10px] text-emerald-400/70">offline</span>
                              </div>
                            )}

                            <div style={{ left: `${onlinePercent}%`, top: '0.875rem' }} className="absolute flex flex-col items-center transform -translate-x-1/2">
                              <span className="text-[11px] text-emerald-300">{onlineVal != null ? onlineVal : '—'}</span>
                              <span className="text-[10px] text-emerald-400/70">online</span>
                            </div>

                            {maxVal != null && (
                              <div style={{ right: '0%', top: '0.875rem' }} className="absolute flex flex-col items-end">
                                <span className="text-[11px] text-emerald-300">{maxVal}</span>
                                <span className="text-[10px] text-emerald-400/70">max</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </>
                  );
                })()}
                
                {!fromFiltered && (
                  <p className="text-xs text-emerald-300/70 mt-2 italic">
                    {usingTierMedianFallback
                      ? `No same-tier (${computedTierLabel}) results in filtered list — using tier-only median (first ${tierSample.length} listings) as minimum.`
                      : "No same-tier filtered results — using reference listings."}
                  </p>
                )}
              </div>
            );
          })()}

          {(results !== null || baseResults !== null) && !loading && (
            <div className="mt-4 border border-slate-800 rounded-2xl p-4 bg-slate-900/70">
              <p className="text-xs text-slate-400 uppercase tracking-wide mb-3 font-semibold">Share / Export</p>
              <div className="flex gap-2">
                <button
                  onClick={copyShareUrl}
                  className="flex-1 flex items-center justify-center gap-2 border border-slate-700 rounded-lg py-2 text-sm font-semibold text-slate-200 hover:bg-slate-800 transition"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor"><path d="M12.586 4.586a2 2 0 112.828 2.828l-3 3a2 2 0 01-2.828 0 1 1 0 00-1.414 1.414 4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.5 1.5a1 1 0 101.414 1.414l1.5-1.5zm-5 5a2 2 0 012.828 0 1 1 0 101.414-1.414 4 4 0 00-5.656 0l-3 3a4 4 0 105.656 5.656l1.5-1.5a1 1 0 10-1.414-1.414l-1.5 1.5a2 2 0 11-2.828-2.828l3-3z" /></svg>
                  {shareCopied === "url" ? "✓ Link copied!" : "Copy link"}
                </button>
                <button
                  onClick={copyTextSummary}
                  disabled={!results}
                  className="flex-1 flex items-center justify-center gap-2 border border-slate-700 rounded-lg py-2 text-sm font-semibold text-slate-200 hover:bg-slate-800 disabled:opacity-40 transition"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor"><path d="M8 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" /><path d="M6 3a2 2 0 00-2 2v11a2 2 0 002 2h8a2 2 0 002-2V5a2 2 0 00-2-2 3 3 0 01-3 3H9a3 3 0 01-3-3z" /></svg>
                  {shareCopied === "text" ? "✓ Copied!" : "Copy for Discord"}
                </button>
              </div>
            </div>
          )}

          
        </div>
      </div>}
      {!consentBannerDismissed && storageConsent !== true && (
        <div className="fixed bottom-0 left-0 right-0 bg-slate-950 text-slate-50 px-6 py-4 flex flex-col sm:flex-row items-start sm:items-center gap-3 shadow-lg z-50 border-t border-sky-500/20">
          <div className="flex-1 text-sm">
            <span className="font-semibold">Local storage</span> — This app can save your last 5 searches locally in your browser to make them quick to re-run.
            No data is sent to any server. You can decline and the app will work normally without saving anything.
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <button
              onClick={() => {
                localStorage.setItem("appraisal_consent", "false");
                setStorageConsent(false);
                setConsentBannerDismissed(true);
              }}
              className="px-3 py-1.5 rounded-lg border border-slate-600 text-slate-300 hover:bg-slate-800 text-sm transition"
            >
              Decline
            </button>
            <button
              onClick={() => {
                localStorage.setItem("appraisal_consent", "true");
                setStorageConsent(true);
                setConsentBannerDismissed(true);
              }}
              className="px-3 py-1.5 rounded-lg bg-sky-600 hover:bg-sky-700 text-white text-sm font-semibold transition"
            >
              Accept
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
